/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import {
    CircleDollarSign,
    FileDown,
    FileChartColumn,
    Info,
    List,
    type LucideIcon,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { PRICE_TRAFFIC_LIGHT_CRITICAL, PRICE_TRAFFIC_LIGHT_URN, PRICE_TRAFFIC_LIGHT_WARNING } from '@/config/app';
import { APP_ROUTES } from '@/config/appRoutes';
import { OVERVIEW_DEVICE_SENSOR_CONFIG } from '@/config/deviceSensorConfig';
import {
    WATER_LOSS_SYSTEM_DASHBOARD_CONFIG,
    type OverviewAnomalyConfig,
    type OverviewAnomalyKpiConfig,
} from '@/config/kpi/overviewAnomalyConfig';
import {
    BASE_LOAD_SEVERITY_STATUS_DEFINITION,
    FIXED_OK_STATUS_DEFINITION,
    HEATING_ENERGY_WEEKEND_REDUCTION_STATUS_DEFINITION,
    PV_DTC_STATUS_DEFINITION,
    WATER_LOSS_DAILY_DTC_STATUS_DEFINITION,
    WATER_LOSS_DTC_STATUS_DEFINITION,
} from '@/config/kpi/statusConfig';

/* Import components */
import Api2LastValueData from '@/components/api/Api2LastValueData';
import AppLoader from '@/components/feedback/AppLoader';
import StatusModal from '@/components/feedback/StatusModal';
import DashboardGrid from '@/components/layout/DashboardGrid';
import SystemInformationModal from '@/components/tabs/overview/system/SystemInformationModal';
import SystemDashboardKpiStatusModal from '@/components/tabs/overview/system/SystemDashboardKpiStatusModal';
import SystemDashboardSectionLabel from '@/components/tabs/overview/system/SystemDashboardSectionLabel';
import SystemDashboardTile from '@/components/tabs/overview/system/SystemDashboardTile';
import {
    ElectricityIcon,
    GasIcon,
    HeatIcon,
    PhotovoltaicsIcon,
    ReportIcon,
    WaterIcon,
} from '@/components/icon/SupplyIcon';

/* Import libraries */
import type { Api2ValueDataItem } from '@/lib/overview/api2ValueData';
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import { roundToSignificantDigits } from '@/lib/formatting/significantDigits';
import {
    getOverviewStatusCategory,
    getStatusSeverityFromTrafficLight,
    type StatusSeverity,
} from '@/lib/overview/overviewCards';
import { createOverviewDataset, type OverviewDataset } from '@/lib/overview/overviewDataset';
import {
    calculateSystemDashboardKpiStatus,
    type SystemDashboardKpiStatusDefinition,
    type SystemDashboardKpiStatusValues,
} from '@/lib/overview/systemDashboardKpiStatus';
import { ReverseDeviceSensor } from '@/lib/sensors/catalog/reverseDeviceSensor';
import { getSystemInformation } from '@/lib/sensors/presentation/systemInformation';
import {
    formatTimeSeriesNumericValue,
    resolveTimeSeriesTrafficLightSeverityDefault,
    resolveTimeSeriesTrafficLightStatusLabel,
} from '@/lib/timeSeries/timeSeriesValue';

/* Import types */
import type { SensorMetadataEntry } from '@/lib/sensors/csv/csvToSensorMetadata';
import type {
    SystemDashboardSectionLabelAccent,
    SystemDashboardSectionLabelAnomalies,
} from '@/components/tabs/overview/system/SystemDashboardSectionLabel';
import type { SystemDashboardTileData } from '@/components/tabs/overview/system/SystemDashboardTile';
import type { DeviceSensorCsvMetadataGroup, DeviceSensorMetadataGroup } from '@/types/deviceSensor';

interface SystemDashboardOverviewProps {
    anomalyConfigs: readonly OverviewAnomalyConfig[];
    systemKey: string;
}

interface SystemDashboardSensor {
    anomalyConfig: OverviewAnomalyConfig;
    deviceName: string;
    item: Api2ValueDataItem;
    metadata: SensorMetadataEntry;
    status: number;
}

interface SystemDashboardKpi {
    deviceName: string;
    formattedValue: string;
    rawValue: number;
    sensor: SystemDashboardSensor;
    sensorType: string;
    unit: string | null;
    urn: string;
}

interface SystemDashboardKpiDisplayConfig {
    conversion?: number;
    formatValue?: (value: number) => string;
    fractionDigits?: number;
    transformValue?: (value: number) => number;
    unit?: string | null;
}

interface SystemDashboardSectionSensor {
    deviceName: string;
    isEnabled: boolean;
}

interface SystemDashboardSectionData {
    accent: SystemDashboardSectionLabelAccent;
    anomalies?: SystemDashboardSectionLabelAnomalies;
    desktopColumns?: number;
    icon: LucideIcon;
    label: string;
    onSensorChange?: (sensor: string) => void;
    sensors?: SystemDashboardSectionSensor[];
    tiles: SystemDashboardTileData[];
    to?: string;
}

const systemDashboardSensorsCache = new WeakMap<
    Api2ValueDataItem[],
    WeakMap<OverviewDataset, Map<string, SystemDashboardSensor[]>>
>();

const TILE_GRID_CLASS_NAMES: Record<number, string> = {
    1: '2xl:grid-cols-1',
    2: '2xl:grid-cols-2',
    3: '2xl:grid-cols-3',
    4: '2xl:grid-cols-4',
    5: '2xl:grid-cols-5',
};

const WATER_LOSS_HOURS_PER_DAY = 24;
const WATER_LOSS_HOURS_PER_SEVEN_DAYS = WATER_LOSS_HOURS_PER_DAY * 7;

const SYSTEM_DASHBOARD_CONSUMPTION_KPI: OverviewAnomalyKpiConfig = {
    conversion: 1,
    fractionDigits: 2,
    sensorGroup: null,
    sensorType: 'W',
    systemAggregation: 'sum',
    unit: null,
};

const SYSTEM_DASHBOARD_WATER_LOSS_CONFIG: OverviewAnomalyConfig = {
    ...WATER_LOSS_SYSTEM_DASHBOARD_CONFIG,
    kpis: [
        ...WATER_LOSS_SYSTEM_DASHBOARD_CONFIG.kpis,
        {
            conversion: 1,
            fractionDigits: 2,
            sensorType: 'qv_loss_1d',
            systemAggregation: 'sum',
            unit: 'm³/h',
        },
        {
            conversion: 1,
            fractionDigits: 0,
            sensorType: 'dtc',
            systemAggregation: 'max',
            unit: null,
        },
    ],
};

function createSystemDashboardAnomalyConfigs(
    anomalyConfigs: readonly OverviewAnomalyConfig[],
): OverviewAnomalyConfig[] {
    const dashboardConfigs = anomalyConfigs.map((anomalyConfig) => {
        if (anomalyConfig.group !== 'BL' && anomalyConfig.group !== 'HE') {
            return anomalyConfig;
        }

        return {
            ...anomalyConfig,
            kpis: [...anomalyConfig.kpis, SYSTEM_DASHBOARD_CONSUMPTION_KPI],
        };
    });

    return [...dashboardConfigs, SYSTEM_DASHBOARD_WATER_LOSS_CONFIG];
}

function getErrorMessage(errorMessage: string | string[]): string {
    return Array.isArray(errorMessage)
        ? errorMessage.find(Boolean) ?? ''
        : errorMessage;
}

function getKpiUrn(item: Api2ValueDataItem, sensorType: string): string | null {
    return item.urn.entries().find((entry) => (
        entry.key === sensorType || entry.aliases?.includes(sensorType)
    ))?.key ?? null;
}

function resolveSystemDashboardSensors(
    items: Api2ValueDataItem[],
    dataset: OverviewDataset,
    deviceSensorCatalog: DeviceSensorCatalog,
    systemKey: string,
): SystemDashboardSensor[] {
    return items.flatMap((item): SystemDashboardSensor[] => {
        const resolvedUrn = item.urn.getFirst();

        if (resolvedUrn === null) {
            return [];
        }

        if (getErrorMessage(item.errorMessage)) {
            return [];
        }

        const anomalyConfig = dataset.definitionsByUrn.get(resolvedUrn);

        if (!anomalyConfig) {
            return [];
        }

        const reverseDeviceSensor = new ReverseDeviceSensor(
            resolvedUrn,
            deviceSensorCatalog,
        );
        const deviceName = reverseDeviceSensor.getVirtualDeviceName();

        if (deviceName === null) {
            return [];
        }

        const metadata = deviceSensorCatalog.getSensorMetadata(
            deviceName,
            anomalyConfig.group,
        );

        if (metadata === null || metadata.system !== systemKey) {
            return [];
        }

        return [{
            anomalyConfig,
            deviceName,
            item,
            metadata,
            status: Number(item.rawValue.get('status') ?? 0),
        }];
    }).sort((leftSensor, rightSensor) => (
        leftSensor.deviceName.localeCompare(rightSensor.deviceName)
    ));
}

function getSystemDashboardSensors(
    items: Api2ValueDataItem[],
    dataset: OverviewDataset,
    deviceSensorCatalog: DeviceSensorCatalog,
    systemKey: string,
): SystemDashboardSensor[] {
    const sensorsByDataset = systemDashboardSensorsCache.get(items);
    const sensorsBySystem = sensorsByDataset?.get(dataset);
    const cachedSensors = sensorsBySystem?.get(systemKey);

    if (cachedSensors) {
        return cachedSensors;
    }

    const sensors = resolveSystemDashboardSensors(
        items,
        dataset,
        deviceSensorCatalog,
        systemKey,
    );
    const nextSensorsBySystem = sensorsBySystem ?? new Map<string, SystemDashboardSensor[]>();
    const nextSensorsByDataset = sensorsByDataset ?? new WeakMap<
        OverviewDataset,
        Map<string, SystemDashboardSensor[]>
    >();

    nextSensorsBySystem.set(systemKey, sensors);
    nextSensorsByDataset.set(dataset, nextSensorsBySystem);
    systemDashboardSensorsCache.set(items, nextSensorsByDataset);

    return sensors;
}

function filterSystemDashboardSensors(
    sensors: SystemDashboardSensor[],
    group: DeviceSensorMetadataGroup,
): SystemDashboardSensor[] {
    return sensors.filter((sensor) => sensor.anomalyConfig.group === group);
}

/** Counts status results exactly as they are categorized in the system sensor details. */
function getSystemDashboardSectionAnomalies(
    sensors: SystemDashboardSensor[],
    group: DeviceSensorMetadataGroup,
): SystemDashboardSectionLabelAnomalies {
    return filterSystemDashboardSensors(sensors, group).reduce<SystemDashboardSectionLabelAnomalies>((counts, sensor) => {
        const statusCategory = getOverviewStatusCategory(sensor.status);

        if (statusCategory === 'danger') {
            return {
                ...counts,
                errorCount: (counts.errorCount ?? 0) + 1,
            };
        }

        if (statusCategory === 'warning') {
            return {
                ...counts,
                warningCount: (counts.warningCount ?? 0) + 1,
            };
        }

        return counts;
    }, {});
}

function getSystemDashboardKpis(
    sensors: SystemDashboardSensor[],
    sensorTypes: string | string[],
): SystemDashboardKpi[] {
    const resolvedSensorTypes = Array.isArray(sensorTypes) ? sensorTypes : [sensorTypes];

    return sensors.flatMap((sensor): SystemDashboardKpi[] => {
        const sensorType = resolvedSensorTypes.find((currentSensorType) => {
            return sensor.item.formattedValue.get(currentSensorType) !== null
                && sensor.item.rawValue.get(currentSensorType) !== null
                && getKpiUrn(sensor.item, currentSensorType) !== null;
        });

        if (!sensorType) {
            return [];
        }

        // BoxValues supports both the short sensor type and the complete URN.
        const formattedValue = sensor.item.formattedValue.get(sensorType)!;
        const rawValue = sensor.item.rawValue.get(sensorType)!;
        const urn = getKpiUrn(sensor.item, sensorType)!;

        return [{
            deviceName: sensor.deviceName,
            formattedValue,
            rawValue,
            sensor,
            sensorType,
            unit: sensor.item.unit.get(sensorType),
            urn,
        }];
    });
}

function getSystemDashboardSectionSensors(
    deviceSensorCatalog: DeviceSensorCatalog,
    systemKey: string,
    group: DeviceSensorCsvMetadataGroup,
): SystemDashboardSectionSensor[] {
    return deviceSensorCatalog.getDevicesBySystem(systemKey)
        .filter((deviceName) => deviceSensorCatalog.getDeviceMetadataGroups(deviceName).includes(group))
        .map((deviceName) => ({
            deviceName,
            isEnabled: deviceSensorCatalog.isMetadataGroupEnabled(deviceName, group),
        }))
        .sort((leftSensor, rightSensor) => {
            if (leftSensor.isEnabled !== rightSensor.isEnabled) {
                return leftSensor.isEnabled ? -1 : 1;
            }

            return leftSensor.deviceName.localeCompare(rightSensor.deviceName);
        });
}

function getSystemDashboardSectionSensorPart(
    deviceSensorCatalog: DeviceSensorCatalog,
    deviceName: string,
): string {
    return new DeviceSensor(deviceName, deviceSensorCatalog).getDeviceSensorPart();
}

function getSystemDashboardKpiStatusValues(
    kpi: SystemDashboardKpi,
): SystemDashboardKpiStatusValues {
    return kpi.sensor.item.rawValue.entries().reduce<SystemDashboardKpiStatusValues>((values, entry) => {
        const valuesWithAliases = {
            ...values,
            [entry.key]: entry.value,
        };

        entry.aliases?.forEach((alias) => {
            valuesWithAliases[alias] = entry.value;
        });

        return valuesWithAliases;
    }, {});
}

function getSystemDashboardKpiStatusSources(
    kpi: SystemDashboardKpi,
    definition: SystemDashboardKpiStatusDefinition,
    unavailableValueLabel: string,
): Array<{
    sensorType: string;
    urn: string;
    value: string;
}> {
    const sourceSensorTypes = Array.from(new Set([
        kpi.sensorType,
        ...definition.sourceSensorTypes,
    ]));

    return sourceSensorTypes.map((sensorType) => {
        const formattedValue = kpi.sensor.item.formattedValue.get(sensorType);
        const unit = kpi.sensor.item.unit.get(sensorType);

        return {
            sensorType,
            urn: getKpiUrn(kpi.sensor.item, sensorType) ?? sensorType,
            value: formattedValue === null
                ? unavailableValueLabel
                : `${formattedValue}${unit ? ` ${unit}` : ''}`,
        };
    });
}

function SystemDashboardSection({
    desktopColumns,
    tiles,
}: Pick<SystemDashboardSectionData, 'desktopColumns' | 'tiles'>) {
    const desktopGridClassName = TILE_GRID_CLASS_NAMES[desktopColumns ?? tiles.length] ?? '2xl:grid-cols-1';

    return (
        <div className={`grid h-full min-h-0 grid-cols-1 gap-3 sm:grid-cols-2 ${desktopGridClassName}`}>
            {tiles.map((tile) => (
                <SystemDashboardTile key={tile.title} {...tile} />
            ))}
        </div>
    );
}

function SystemDashboardOverview({
    anomalyConfigs,
    systemKey,
}: SystemDashboardOverviewProps) {
    const { locale, t } = useI18n();
    const [isComingSoonModalOpen, setIsComingSoonModalOpen] = useState(false);
    const [isSystemInformationModalOpen, setIsSystemInformationModalOpen] = useState(false);
    const [selectedSectionSensors, setSelectedSectionSensors] = useState<Partial<
        Record<SystemDashboardSectionLabelAccent, string>
    >>({});
    const deviceSensorCatalog = useMemo(() => {
        return new DeviceSensorCatalog(OVERVIEW_DEVICE_SENSOR_CONFIG);
    }, []);
    const systemInformation = useMemo(() => {
        return getSystemInformation(deviceSensorCatalog, systemKey);
    }, [deviceSensorCatalog, systemKey]);
    const systemOverviewTitle = systemInformation.description ?? systemKey;
    const sectionSensors = useMemo(() => ({
        electricity: getSystemDashboardSectionSensors(deviceSensorCatalog, systemKey, 'BL'),
        gas: getSystemDashboardSectionSensors(deviceSensorCatalog, systemKey, 'GAS'),
        heat: getSystemDashboardSectionSensors(deviceSensorCatalog, systemKey, 'HE'),
        photovoltaics: getSystemDashboardSectionSensors(deviceSensorCatalog, systemKey, 'PV'),
        water: getSystemDashboardSectionSensors(deviceSensorCatalog, systemKey, 'WATER'),
    }), [deviceSensorCatalog, systemKey]);
    const systemDashboardAnomalyConfigs = useMemo(() => {
        return createSystemDashboardAnomalyConfigs(anomalyConfigs);
    }, [anomalyConfigs]);
    const overviewDataset = useMemo(() => {
        return createOverviewDataset(deviceSensorCatalog, systemDashboardAnomalyConfigs, { system: systemKey });
    }, [deviceSensorCatalog, systemDashboardAnomalyConfigs, systemKey]);
    const systemDashboardRequest = useMemo(() => ({
        calc: [...overviewDataset.calc, undefined],
        conversion: [...overviewDataset.conversion, [1]],
        fractionDigits: [...overviewDataset.fractionDigits, [2]],
        title: [...overviewDataset.title, [null]],
        unit: [...overviewDataset.unit, ['€/MWh']],
        urn: [...overviewDataset.urn, [PRICE_TRAFFIC_LIGHT_URN]],
    }), [overviewDataset]);

    function createKpiTile(
        sensors: SystemDashboardSensor[],
        title: string,
        group: DeviceSensorMetadataGroup,
        sensorType: string | string[],
        statusDefinition: SystemDashboardKpiStatusDefinition,
        display?: SystemDashboardKpiDisplayConfig,
        selectedSensor?: SystemDashboardSectionSensor,
    ): SystemDashboardTileData {
        const systemAnomalyDetailRoute = getSystemAnomalyDetailRoute(group, selectedSensor?.deviceName);

        if (!selectedSensor) {
            return {
                severity: 'inactive',
                title,
                value: t('No sensor or sensors available'),
            };
        }

        if (!selectedSensor.isEnabled) {
            return {
                severity: 'inactive',
                title,
                value: t('Not monitored'),
            };
        }

        const groupSensors = filterSystemDashboardSensors(sensors, group);
        const filteredSensors = groupSensors.filter((sensor) => (
            sensor.deviceName === selectedSensor.deviceName
        ));
        const kpis = getSystemDashboardKpis(filteredSensors, sensorType);
        const kpi = kpis[0];

        /* No sensor was found -> Show an inactive tile with an actionable explanation. */
        if (!kpi) {
            return {
                severity: 'inactive',
                title: title,
                value: t('Sensor not available or not enabled'),
                detail: ' ',
                to: systemAnomalyDetailRoute,
            };
        }

        const status = calculateSystemDashboardKpiStatus(
            statusDefinition,
            getSystemDashboardKpiStatusValues(kpi),
        );

        /* Calculate severity */
        const severity = getStatusSeverityFromTrafficLight(
            resolveTimeSeriesTrafficLightSeverityDefault(status, statusDefinition.trafficLight),
        );

        /* Calculate status label */
        const statusLabel = t(resolveTimeSeriesTrafficLightStatusLabel(status, statusDefinition.trafficLight));
        const shouldFormatDisplayValue = display?.conversion !== undefined
            || display?.fractionDigits !== undefined
            || display?.transformValue !== undefined;
        const displayRawValue = kpi.rawValue * (display?.conversion ?? 1);
        const transformedDisplayValue = display?.transformValue
            ? display.transformValue(displayRawValue)
            : displayRawValue;
        const displayValue = display?.formatValue
            ? display.formatValue(displayRawValue)
            : shouldFormatDisplayValue
                ? formatTimeSeriesNumericValue(
                    transformedDisplayValue,
                    locale,
                    display?.fractionDigits,
                )
                : kpi.formattedValue;
        const displayUnit = display?.unit === undefined ? kpi.unit : display.unit;
        const resolvedDisplayValue = `${displayValue}${displayUnit ? ` ${displayUnit}` : ''}`;

        return {
            detail: kpi.urn,
            detailIsSensor: true,
            modal: {
                content: (
                    <SystemDashboardKpiStatusModal
                        definition={statusDefinition}
                        displayValue={resolvedDisplayValue}
                        isDisplayValueTransformed={display?.transformValue !== undefined}
                        sources={getSystemDashboardKpiStatusSources(
                            kpi,
                            statusDefinition,
                            t('Not available'),
                        )}
                        status={status}
                    />
                ),
                title: t('Status calculation'),
            },
            severity,
            statusLabel,
            title,
            to: getSystemAnomalyDetailRoute(group, kpi.deviceName),
            unit: displayUnit,
            value: displayValue,
        };
    }

    function getSystemAnomalyDetailRoute(
        group: DeviceSensorMetadataGroup,
        deviceName: string | undefined,
    ): string | undefined {
        if (!deviceName) {
            return undefined;
        }

        const routeSegmentByGroup: Partial<Record<DeviceSensorMetadataGroup, string>> = {
            BL: 'base-load',
            HE: 'heating-energy',
            PV: 'pv-prediction',
            WL: 'water',
        };
        const routeSegment = routeSegmentByGroup[group];

        return routeSegment
            ? `${APP_ROUTES.systems}/${encodeURIComponent(systemKey)}/${routeSegment}/${encodeURIComponent(deviceName)}`
            : undefined;
    }

    function createMockupTile(
        title: string,
        selectedSensor?: SystemDashboardSectionSensor,
    ): SystemDashboardTileData {
        return {
            severity: 'inactive',
            title,
            value: selectedSensor
                ? t('Not monitored')
                : t('No sensor or sensors available'),
            detail: t('Note: This is only a mockup.'),
        };
    }

    function renderDashboard(items: Api2ValueDataItem[]): ReactNode {
        const sensors = getSystemDashboardSensors(
            items,
            overviewDataset,
            deviceSensorCatalog,
            systemKey,
        );
        const sensorCount = new Set(
            sensors.map((sensor) => sensor.deviceName),
        ).size;
        const priceItem = items.find((item) => item.urn.getFirst() === PRICE_TRAFFIC_LIGHT_URN);
        const price = priceItem?.rawValue.getFirst() ?? null;
        const priceSeverity: StatusSeverity = price === null
            ? 'inactive'
            : price > PRICE_TRAFFIC_LIGHT_CRITICAL
                ? 'error'
                : price >= PRICE_TRAFFIC_LIGHT_WARNING
                    ? 'warning'
                    : 'ok';
        const priceValue = priceItem?.formattedValue.getFirst();
        const priceUnit = priceItem?.unit.getFirst();
        const priceStatusLabel = priceSeverity === 'error'
            ? t('Expensive')
            : priceSeverity === 'warning'
                ? t('Normal')
                : priceSeverity === 'ok'
                    ? t('Favourable')
                    : undefined;
        const getSelectedSectionSensor = (
            accent: Exclude<SystemDashboardSectionLabelAccent, 'report'>,
        ): SystemDashboardSectionSensor | undefined => {
            const availableSensors = sectionSensors[accent];
            const selectedSensor = selectedSectionSensors[accent];

            return selectedSensor
                ? availableSensors.find((sensor) => sensor.deviceName === selectedSensor) ?? availableSensors[0]
                : availableSensors[0];
        };
        const selectSectionSensor = (
            accent: Exclude<SystemDashboardSectionLabelAccent, 'report'>,
            sensor: string,
        ) => {
            setSelectedSectionSensors((currentSensors) => ({
                ...currentSensors,
                [accent]: sensor,
            }));
        };
        const selectedPhotovoltaicsSensor = getSelectedSectionSensor('photovoltaics');
        const selectedElectricitySensor = getSelectedSectionSensor('electricity');
        const selectedHeatSensor = getSelectedSectionSensor('heat');
        const selectedWaterSensor = getSelectedSectionSensor('water');
        const systemDetailsRoute = `${APP_ROUTES.systems}/${encodeURIComponent(systemKey)}/detail`;

        /* Tiles configuration for /systems/:systemKey */
        const sections: SystemDashboardSectionData[] = [
            /* IMPORTANT PLACE: A) Photovoltaik/photovoltaics configuration */
            {
                accent: 'photovoltaics',
                anomalies: getSystemDashboardSectionAnomalies(sensors, 'PV'),
                desktopColumns: 3,
                icon: PhotovoltaicsIcon,
                label: t('Photovoltaics'),
                onSensorChange: (sensor) => selectSectionSensor('photovoltaics', sensor),
                sensors: sectionSensors.photovoltaics,
                to: `${systemDetailsRoute}?anomaly=pv`,
                tiles: [
                    createKpiTile(
                        sensors,
                        t('Status'),
                        'PV',
                        'dtc',
                        PV_DTC_STATUS_DEFINITION,
                        {
                            formatValue: (value) => value >= 200
                                ? t('Error')
                                : value >= 100
                                    ? t('Warning')
                                    : t('OK'),
                            unit: null,
                        },
                        selectedPhotovoltaicsSensor,
                    ),
                ],
            },

            /* IMPORTANT PLACE: B) Strom/electricity configuration */
            {
                accent: 'electricity',
                anomalies: getSystemDashboardSectionAnomalies(sensors, 'BL'),
                icon: ElectricityIcon,
                label: t('Electricity'),
                onSensorChange: (sensor) => selectSectionSensor('electricity', sensor),
                sensors: sectionSensors.electricity,
                to: `${systemDetailsRoute}?anomaly=bl`,
                tiles: [
                    createKpiTile(
                        sensors,
                        t('Consumption'),
                        'BL',
                        ['W', 'W_1d'],
                        FIXED_OK_STATUS_DEFINITION,
                        {
                            conversion: 1 / 1000,
                            transformValue: (value) => roundToSignificantDigits(value, 2),
                            unit: 'MWh',
                        },
                        selectedElectricitySensor,
                    ),
                    createKpiTile(
                        sensors,
                        t('Base Load'),
                        'BL',
                        'Min',
                        BASE_LOAD_SEVERITY_STATUS_DEFINITION,
                        {
                            transformValue: (value) => roundToSignificantDigits(value, 2),
                        },
                        selectedElectricitySensor,
                    ),
                    createMockupTile(t('Peak Load'), selectedElectricitySensor),
                ],
            },

            /* IMPORTANT PLACE: C) Wärme/heat configuration */
            {
                accent: 'heat',
                anomalies: getSystemDashboardSectionAnomalies(sensors, 'HE'),
                icon: HeatIcon,
                label: t('Heat'),
                onSensorChange: (sensor) => selectSectionSensor('heat', sensor),
                sensors: sectionSensors.heat,
                to: `${systemDetailsRoute}?anomaly=heating_energy`,
                tiles: [
                    createKpiTile(
                        sensors,
                        t('Consumption'),
                        'HE',
                        ['W', 'W_1d'],
                        FIXED_OK_STATUS_DEFINITION,
                        {
                            conversion: 1 / 1000,
                            transformValue: (value) => roundToSignificantDigits(value, 2),
                            unit: 'MWh',
                        },
                        selectedHeatSensor,
                    ),
                    createKpiTile(
                        sensors,
                        t('Weekend Setback'),
                        'HE',
                        'WeekendReductionPercent',
                        HEATING_ENERGY_WEEKEND_REDUCTION_STATUS_DEFINITION,
                        {
                            transformValue: (value) => roundToSignificantDigits(value, 2),
                        },
                        selectedHeatSensor,
                    ),
                    createMockupTile(t('Night Setback'), selectedHeatSensor),
                    createMockupTile(t('Simultaneous Heating and Cooling'), selectedHeatSensor),
                ],
            },

            /* IMPORTANT PLACE: D) Wasser/water configuration */
            {
                accent: 'water',
                anomalies: getSystemDashboardSectionAnomalies(sensors, 'WL'),
                desktopColumns: 3,
                icon: WaterIcon,
                label: t('Water'),
                onSensorChange: (sensor) => selectSectionSensor('water', sensor),
                sensors: sectionSensors.water,
                to: `${systemDetailsRoute}?anomaly=water_bl`,
                tiles: [
                    createKpiTile(
                        sensors,
                        t('Average water loss last day'),
                        'WL',
                        'qv_loss_1d',
                        WATER_LOSS_DAILY_DTC_STATUS_DEFINITION,
                        {
                            conversion: WATER_LOSS_HOURS_PER_DAY,
                            transformValue: (value) => roundToSignificantDigits(value, 2),
                            unit: 'm³ / Tag',
                        },
                        selectedWaterSensor,
                    ),
                    createKpiTile(
                        sensors,
                        t('Average water loss last 7 days'),
                        'WL',
                        'qv_loss_7d',
                        WATER_LOSS_DTC_STATUS_DEFINITION,
                        {
                            conversion: WATER_LOSS_HOURS_PER_SEVEN_DAYS,
                            transformValue: (value) => roundToSignificantDigits(value, 2),
                            unit: 'm³ / 7 Tage',
                        },
                        selectedWaterSensor,
                    ),
                ],
            },

            /* IMPORTANT PLACE: E) Gas/gas configuration */
            {
                accent: 'gas',
                anomalies: {},
                desktopColumns: 3,
                icon: GasIcon,
                label: t('Gas'),
                onSensorChange: (sensor) => selectSectionSensor('gas', sensor),
                sensors: sectionSensors.gas,
                to: `${systemDetailsRoute}?anomaly=gas_bl`,
                tiles: [createMockupTile(t('Basic Consumption'), getSelectedSectionSensor('gas'))],
            },

            /* IMPORTANT PLACE: F) Report/report configuration */
            {
                accent: 'report',
                icon: ReportIcon,
                label: t('Report'),
                tiles: [
                    {
                        severity: 'inactive',
                        title: t('Savings Potential'),
                        onClick: () => setIsComingSoonModalOpen(true),
                        value: t('Not implemented yet.'),
                    },
                    {
                        severity: 'inactive',
                        title: t('Data Outage'),
                        onClick: () => setIsComingSoonModalOpen(true),
                        value: t('Not implemented yet.'),
                    },
                    {
                        actionIcon: CircleDollarSign,
                        severity: priceSeverity,
                        statusLabel: priceStatusLabel,
                        title: t('Price Indicator'),
                        to: `${APP_ROUTES.systems}/${encodeURIComponent(systemKey)}/price-traffic-light`,
                        value: priceValue ? `${priceValue}${priceUnit ? ` ${priceUnit}` : ''}` : '',
                    },
                    {
                        actionIcon: FileChartColumn,
                        severity: 'ok',
                        title: t('Statistics'),
                        to: `${APP_ROUTES.systems}/${encodeURIComponent(systemKey)}/statistics`,
                        value: '',
                    },
                    {
                        actionIcon: FileDown,
                        severity: 'inactive',
                        title: t('Export PDF'),
                        onClick: () => setIsComingSoonModalOpen(true),
                        value: '',
                    },
                ],
            },
        ];

        return (
            <>
                <DashboardGrid.Item
                    className="grid min-h-0 grid-cols-1 items-center gap-2 2xl:grid-cols-3"
                    desktopColSpan={2}
                >
                    <h1 className="text-xl font-semibold 2xl:justify-self-start">
                        {t('System overview "{key}"', { key: systemOverviewTitle })}
                    </h1>
                    <p className="mb-0 text-center text-sm text-base-content/65 2xl:justify-self-center">
                        {t('{count} configured sensors', { count: sensorCount })}
                    </p>
                    <div className="flex flex-wrap justify-center gap-2 2xl:justify-self-end">
                        <button
                            className="btn btn-sm btn-outline gap-2"
                            type="button"
                            onClick={() => setIsSystemInformationModalOpen(true)}
                        >
                            <Info className="h-4 w-4" aria-hidden="true" />
                            {t('View system information')}
                        </button>
                        <NavLink
                            className="btn btn-sm btn-outline gap-2"
                            to={`${APP_ROUTES.systems}/${encodeURIComponent(systemKey)}/detail`}
                        >
                            <List className="h-4 w-4" aria-hidden="true" />
                            {t('View sensor details')}
                        </NavLink>
                    </div>
                </DashboardGrid.Item>

                {sections.flatMap((section) => {
                    const sensors = section.sensors?.map((sensor) => ({
                        ...sensor,
                        sensorPart: getSystemDashboardSectionSensorPart(
                            deviceSensorCatalog,
                            sensor.deviceName,
                        ),
                    }));

                    return [
                        <DashboardGrid.Item key={`${section.label}-label`} mobileMinHeight="80px">
                            <SystemDashboardSectionLabel
                                key={`${section.accent}-${section.sensors?.map(({ deviceName }) => deviceName).join('|') ?? 'without-sensors'}`}
                                accent={section.accent}
                                anomalies={section.anomalies}
                                disabledSensors={sensors
                                    ?.filter(({ isEnabled }) => !isEnabled)
                                    .map(({ sensorPart }) => sensorPart)}
                                icon={section.icon}
                                label={section.label}
                                onSensorChange={(sensorPart) => {
                                    const sensor = sensors?.find((item) => item.sensorPart === sensorPart);

                                    if (sensor) {
                                        section.onSensorChange?.(sensor.deviceName);
                                    }
                                }}
                                sensors={sensors?.map(({ sensorPart }) => sensorPart)}
                                to={section.to}
                            />
                        </DashboardGrid.Item>,
                        <DashboardGrid.Item key={`${section.label}-tiles`} mobileMinHeight="112px">
                            <SystemDashboardSection
                                desktopColumns={section.desktopColumns}
                                tiles={section.tiles}
                            />
                        </DashboardGrid.Item>,
                    ];
                })}
            </>
        );
    }

    return (
        <>
            <DashboardGrid
                aria-label={t('System overview "{key}"', { key: systemKey })}
                baseAutoRows="auto"
                breakpoint="2xl"
                desktopCols={['minmax(10rem, 0.65fr)', 'minmax(0, 4fr)']}
                desktopRows={['auto', 'repeat(6, minmax(0, 1fr))']}
            >
                <Api2LastValueData
                    date="NOW"
                    urn={systemDashboardRequest.urn}
                    calc={systemDashboardRequest.calc}
                    conversion={systemDashboardRequest.conversion}
                    fractionDigits={systemDashboardRequest.fractionDigits}
                    title={systemDashboardRequest.title}
                    unit={systemDashboardRequest.unit}
                >
                    {({ items, isLoading, loadingLabel }) => {
                        if (isLoading) {
                            return (
                                <DashboardGrid.Item
                                    className="flex h-full min-h-80 items-center justify-center"
                                    desktopColSpan={2}
                                    desktopRowSpan={7}
                                >
                                    <AppLoader label={loadingLabel || t('Loading API request...')} />
                                </DashboardGrid.Item>
                            );
                        }

                        return renderDashboard(items);
                    }}
                </Api2LastValueData>
            </DashboardGrid>

            <StatusModal
                isOpen={isComingSoonModalOpen}
                message={t('This feature will be available soon.')}
                onClose={() => setIsComingSoonModalOpen(false)}
                title={t('Coming soon')}
            />

            <SystemInformationModal
                information={systemInformation}
                isOpen={isSystemInformationModalOpen}
                onClose={() => setIsSystemInformationModalOpen(false)}
            />
        </>
    );
}

export default SystemDashboardOverview;
